import React, { useState, useEffect } from 'react'
import { styled } from 'styled-components'

import type { CollaborativeCulminatingMomentScreen as CollaborativeCulminatingMomentScreenType } from '@mission.io/mission-toolkit'
import type { Image, Video } from '@mission.io/mission-toolkit/actions'
import { COLLABORATIVE_CULMINATING_MOMENT } from '@mission.io/mission-toolkit/constants'

import ConnectedVideo from '../../../basics/ConnectedVideo'
import { CollaborativeCulminatingMomentGrading } from './CollaborativeCulminatingMomentGrading'
import { CollaborativeCulminatingMomentDisplayResults } from './CollaborativeCulminatingMomentDisplayResults'
import { CollaborativeCulminatingVoting } from './CollaborativeCulminatingMomentVoting'
import BasicFrame from '../../../frames/BasicFrame'

/**
 * The collaborative culminating moment screen. Performs all work necessary to progress the screen to completion. Also allows the user to grade the related documents.
 */
export function CollaborativeCulminatingMomentScreen({
	screen,
}: {
	screen: CollaborativeCulminatingMomentScreenType,
}): React$Node {
	const [completedVoteReveal, setCompletedVoteReveal] = React.useState(false)
	const [votingScreenDocuments, setVotingScreenDocuments] = useState()
	useEffect(() => {
		if (
			screen.currentStep.status ===
			COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.VOTING
		) {
			setVotingScreenDocuments(screen.currentStep.qualifiedDocuments)
		}
	}, [screen])

	// If the screen step was voting, but is now grading unhidden or display results show the voting results, if voting results are complete then show other things
	const showVoteReveal =
		Boolean(votingScreenDocuments) &&
		(screen.currentStep.status ===
			COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_HIDDEN ||
			screen.currentStep.status ===
				COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.DISPLAY_RESULTS) &&
		!completedVoteReveal
	if (
		screen.currentStep.status ===
			COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.VOTING ||
		showVoteReveal
	) {
		return (
			<Container>
				<CollaborativeCulminatingVoting
					screen={screen}
					onComplete={() => setCompletedVoteReveal(true)}
					qualifiedDocuments={
						showVoteReveal
							? // $FlowFixMe - we've already checked the screen step and we know there are qualified documents
							  votingScreenDocuments
							: // $FlowFixMe - we've already checked the screen step and we know there are qualified documents
							  screen.currentStep.qualifiedDocuments
					}
				/>
			</Container>
		)
	}

	return (
		<Container>
			{screen.currentStep.status ===
			COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.EDITING ? (
				<EditingStep screenMedia={screen.currentStep.screenMedia} />
			) : screen.currentStep.status ===
					COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_UNHIDDEN ||
			  screen.currentStep.status ===
					COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_HIDDEN ||
			  screen.currentStep.status ===
					COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_ALL ? (
				<CollaborativeCulminatingMomentGrading
					screen={screen}
					gradingStep={screen.currentStep}
				/>
			) : screen.currentStep.status ===
			  COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.DISPLAY_RESULTS ? (
				<CollaborativeCulminatingMomentDisplayResults
					screen={screen}
					displayStep={screen.currentStep}
				/>
			) : (
				<Centered>
					Unknown Status
					<br />
					Try refreshing the tab
				</Centered>
			)}
		</Container>
	)
}

/**
 * The editing step of the collaborative culminating moment screen. Displays `screenMedia` if it is not null, otherwise shows a default message.
 */
function EditingStep({ screenMedia }: { screenMedia: Image | Video | null }) {
	if (screenMedia?.type === 'IMAGE') {
		return (
			<EditingImage
				src={screenMedia.url}
				alt="Collaborative Culminating Moment Editing Step"
			/>
		)
	}

	if (screenMedia?.type === 'VIDEO') {
		return (
			<ConnectedVideo
				url={screenMedia.url}
				loop
				data-testid="collaborative-culminating-moment-editing-video"
			/>
		)
	}

	return (
		<BasicFrame>
			<Centered>
				<h1 className="font-game-headline">
					Work as a team to&nbsp;create&nbsp;a&nbsp;solution!
				</h1>
			</Centered>
		</BasicFrame>
	)
}

const EditingImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: contain;
`

const Centered = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
	text-align: center;

	> h1 {
		font-size: 3rem;
		line-height: 1.4em;
	}
`

const Container = styled.div`
	height: 100%;
	width: 100%;
	font-size: 1rem;
`
